/**
 * BillingSectionRow — the one billing affordance every application's settings
 * window carries (Plan 02 D3, last row): a read-only plan/status row with the
 * account's tier chip, the overage (402) banner for any metered service at or
 * past its threshold, and the "Manage plan…" action that opens the hub's
 * billing tab. Applications adopt it with one render line; every fact comes
 * from the hub's read plane (the D3 rule: apps consume snapshots, the server
 * stays the enforcement point) and the only state owned here is the checkout
 * sheet's open/closed.
 *
 * The reads are mount-fresh (not the 5-minute cache) — the settings surface
 * is the account's authoritative billing view, so a just-confirmed payment or
 * a dev simulation is visible the moment the row appears.
 */
import { useCallback, useEffect, useState } from "react";
import { billingClient } from "../client/billingClient";
import { invalidateBillingCache } from "../hooks/billingCache";
import { openHubBillingTab } from "../hubOrigin";
import { BillingLabelsProvider, useBillingLabels, type BillingLabels } from "../strings";
import type { Plan, SubscriptionSummary, UsageResponse } from "../types";
import { PlanBadge } from "./PlanBadge";
import { PaywallCard } from "./PaywallCard";
import { UpgradeSheet } from "./UpgradeSheet";
import { serviceLabel } from "./usageLabels";

export interface BillingSectionRowProps {
  /** The application's i18n seam — its `billing.*` locale subtree mapped onto
   *  the kit's label shape. Omitted, the kit's English defaults render. */
  labels?: Partial<BillingLabels>;
  /** Overrides the default "open the hub billing tab" action. */
  onManagePlan?: () => void;
}

/** One metered service currently at or past its threshold, with the pack the
 *  catalog sells for it (null → the banner renders display-only). */
interface OverageEntry {
  serviceId: string;
  label: string;
  pack: Plan | null;
}

function overagesOf(labels: BillingLabels, usage: UsageResponse, packs: Plan[]): OverageEntry[] {
  const entries: OverageEntry[] = [];
  for (const service of usage.services) {
    if (service.unavailable) continue;
    const over = service.meters.some((meter) => meter.source !== "policy" && meter.limit > 0 && meter.used >= meter.limit);
    if (!over) continue;
    entries.push({
      serviceId: service.serviceId,
      label: serviceLabel(labels, service.serviceId),
      pack: packs.find((plan) => plan.serviceId === service.serviceId && plan.billingCycle === "one_time") ?? null,
    });
  }
  return entries;
}

function RowInner({ onManagePlan }: { onManagePlan?: () => void }) {
  const labels = useBillingLabels();
  const [subscription, setSubscription] = useState<SubscriptionSummary | null>(null);
  const [usage, setUsage] = useState<UsageResponse | null>(null);
  const [packs, setPacks] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkoutPlan, setCheckoutPlan] = useState<Plan | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [subscriptionAnswer, usageAnswer, plansAnswer] = await Promise.all([
        billingClient.getSubscription(),
        billingClient.getUsage(),
        billingClient.getPlans(),
      ]);
      setSubscription(subscriptionAnswer.subscriptions[0] ?? null);
      setUsage(usageAnswer);
      setPacks(plansAnswer.plans);
    } catch (err) {
      setError(err instanceof Error ? err.message : "The billing service is unreachable.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Mount-only: the settings surface re-reads fresh every time it appears.
  useEffect(() => {
    void reload();
  }, [reload]);

  const managePlan = useCallback(() => {
    if (onManagePlan) onManagePlan();
    else openHubBillingTab();
  }, [onManagePlan]);

  const overages = usage ? overagesOf(labels, usage, packs) : [];

  return (
    <section className="suite-billing-card" aria-label={labels.billingSectionTitle}>
      <div className="suite-billing-card-head">
        <span className="suite-billing-card-title">{labels.billingSectionTitle}</span>
        {/* The empty badge reserves the chip's box while the read is in
            flight — the row never shifts when the tier lands. */}
        {loading && !subscription ? (
          <span className="suite-billing-badge" aria-hidden="true" />
        ) : (
          <PlanBadge tier={subscription?.tier ?? null} status={subscription?.status ?? null} />
        )}
      </div>

      {error && (
        <div className="suite-billing-error">
          <span>{error}</span>
          <button type="button" className="suite-billing-button" onClick={() => void reload()}>
            {labels.retry}
          </button>
        </div>
      )}

      {overages.map((entry) => (
        <PaywallCard
          key={entry.serviceId}
          service={entry.label}
          variant="inline-banner"
          onUpgrade={entry.pack ? () => setCheckoutPlan(entry.pack) : undefined}
        />
      ))}

      <div className="suite-billing-row-actions">
        <button type="button" className="suite-billing-button" onClick={managePlan}>
          {labels.managePlan}
        </button>
      </div>

      <UpgradeSheet
        open={checkoutPlan !== null}
        plan={checkoutPlan}
        onClose={() => setCheckoutPlan(null)}
        onSuccess={() => {
          // The server's meters/subscription moved — the row re-reads from
          // the network (the sheet already dropped the kit's cache).
          invalidateBillingCache();
          void reload();
        }}
      />
    </section>
  );
}

export function BillingSectionRow({ labels, onManagePlan }: BillingSectionRowProps) {
  const row = <RowInner onManagePlan={onManagePlan} />;
  return labels ? <BillingLabelsProvider labels={labels}>{row}</BillingLabelsProvider> : row;
}
